/**
 * The line above a control chart: how its limits were set (the method, in words, with a sentence on hover),
 * which limits version is in force, and whether those limits are provisional or rest on an unstable baseline.
 * Without limits it says so. The legend names only the marks the chart actually shows.
 */
import { Badge } from "@/lib/ui/badge";
import { spcMethodLabel, type SpcSeries } from "@/lib/api/spc";
import type { SpcPlot } from "./plot";
import { SERIES_PALETTE } from "../../series-palette";

const PROVISIONAL =
  "These limits come from less data than a full baseline. The analysis replaces them once enough readings exist.";

const BADGE = "px-1.5 py-0 text-[10px]";

export function SpcChartHeader({
  series,
  plot,
  reference,
  limitsHidden,
}: {
  series?: SpcSeries;
  plot: SpcPlot;
  reference: boolean;
  /** Why the limits are hidden (spcLimitsHiddenText), or null when they are drawn. */
  limitsHidden?: string | null;
}) {
  const limits = series?.limits;
  const method = limits ? spcMethodLabel(limits.method) : null;
  const provisionalWhy = limits?.excluded?.provisional_why || series?.phase_one?.provisional_why || PROVISIONAL;
  const unstableWhy = limits?.excluded?.unstable_why;
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-on-surface-muted">
      {method && (
        <span
          title={method.detail}
          className="rounded-md border border-border bg-surface-muted px-1.5 py-px font-medium text-on-surface"
        >
          {method.label}
        </span>
      )}
      {limits && <span>Limits v{limits.version}</span>}
      {limits?.provisional && (
        <Badge variant="warning" className={BADGE} title={provisionalWhy}>
          Provisional
        </Badge>
      )}
      {unstableWhy && (
        <Badge variant="danger" className={BADGE} title={unstableWhy}>
          Unstable baseline
        </Badge>
      )}
      {!plot.hasLimits && (
        <span className="font-medium text-on-surface" title={limitsHidden ?? undefined}>
          {limitsHidden ? "Limits hidden" : "No control limits"}
        </span>
      )}
      <SpcLegend plot={plot} reference={reference} />
    </div>
  );
}

type SwatchKind = "center" | "limits" | "band" | "signal" | "censored" | "pre" | "lower" | "ideal";

function Swatch({ kind }: { kind: SwatchKind }) {
  switch (kind) {
    case "band":
      return <span className="inline-block h-2 w-3 rounded-sm bg-info/20" />;
    case "signal":
      return <span className="inline-block size-2 rounded-full bg-danger" />;
    case "pre":
      return <span className="inline-block size-2 rounded-full bg-on-surface-muted/50" />;
    case "censored":
      return (
        <svg width="10" height="9" aria-hidden="true">
          <path d="M5,1 L9,8 L1,8 Z" fill="none" stroke="rgb(var(--color-warning))" strokeWidth="1.5" />
        </svg>
      );
    default: {
      const stroke = {
        center: "rgb(var(--color-on-surface-muted))",
        limits: "rgb(var(--color-danger))",
        lower: SERIES_PALETTE[1],
        ideal: "rgb(var(--color-accent))",
      }[kind];
      const dash = kind === "center" ? "3 2" : kind === "limits" ? "4 2" : undefined;
      return (
        <svg width="14" height="6" aria-hidden="true">
          <line x1="0" y1="3" x2="14" y2="3" stroke={stroke} strokeWidth="1.5" strokeDasharray={dash} />
        </svg>
      );
    }
  }
}

function SpcLegend({ plot, reference }: { plot: SpcPlot; reference: boolean }) {
  const items: Array<[SwatchKind, string]> = [];
  if (plot.hasLimits && plot.rows.some((r) => r.center !== null)) items.push(["center", "center line"]);
  if (plot.hasLimits) items.push(["limits", plot.kind === "cusum" ? "decision interval" : "control limits"]);
  if (plot.hasLower) items.push(["lower", "lower sum"]);
  if (plot.band) items.push(["band", "baseline window"]);
  if (plot.rows.some((r) => r.flagged)) items.push(["signal", "signal"]);
  if (plot.hasCensored) items.push(["censored", "at a sensor limit"]);
  if (plot.hasPre) items.push(["pre", "before the baseline"]);
  if (reference) items.push(["ideal", "ideal"]);
  if (items.length === 0) return null;
  return (
    <span className="ml-auto flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px]">
      {items.map(([kind, text]) => (
        <span key={kind} className="inline-flex items-center gap-1">
          <Swatch kind={kind} />
          {text}
        </span>
      ))}
    </span>
  );
}
